import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import Layout from '../components/Layout'
import { api } from '../lib/api'
import { useAlert } from '../components/AlertProvider'
import { ALL_QUESTIONS, ProfileQuestion } from '../config/profileQuestions'

export default function ProfilePage() {
  const { t, i18n } = useTranslation()
  const { user, profile, setProfile } = useStore()
  const { showAlert, showConfirm } = useAlert()
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [likeCount, setLikeCount] = useState(0)
  const [showLikes, setShowLikes] = useState(false)
  const [likes, setLikes] = useState<any[]>([])
  const [photos, setPhotos] = useState<any[]>([])
  const [profileAnswers, setProfileAnswers] = useState<Record<string, string>>({})
  const [expandedStage, setExpandedStage] = useState<number | null>(null)

  const [formData, setFormData] = useState<{
    display_name: string
    bio: string
    birth_year: string | number
    gender: string
    country: string
    city: string
    mbti: string
    interests: string
    languages: string
    preferred_gender: string
    preferred_age_min: number
    preferred_age_max: number
  }>({
    display_name: profile?.display_name || user?.username || '',
    bio: profile?.bio || '',
    birth_year: profile?.birth_year || '',
    gender: profile?.gender || '',
    country: profile?.country || 'KR',
    city: profile?.city || '',
    mbti: profile?.mbti || '',
    interests: profile?.interests?.join(', ') || '',
    languages: profile?.languages?.join(', ') || 'ko',
    preferred_gender: profile?.preferred_gender || 'any',
    preferred_age_min: profile?.preferred_age_min || 20,
    preferred_age_max: profile?.preferred_age_max || 40,
  })

  useEffect(() => {
    if (!user?.id) return;

    const fetchProfileData = async () => {
      try {
        // Fetch profile
        const profileData = await api.users.getProfile()
        setProfile(profileData)
        setFormData({
          display_name: profileData.display_name || user?.username || '',
          bio: profileData.bio || '',
          birth_year: profileData.birth_year || '',
          gender: profileData.gender || '',
          country: profileData.country || 'KR',
          city: profileData.city || '',
          mbti: profileData.mbti || '',
          interests: profileData.interests?.join(', ') || '',
          languages: profileData.languages?.join(', ') || 'ko',
          preferred_gender: profileData.preferred_gender || 'any',
          preferred_age_min: profileData.preferred_age_min || 20,
          preferred_age_max: profileData.preferred_age_max || 40,
        })

        // Fetch likes
        const likesData = await api.users.getLikes()
        setLikeCount(likesData.length)
        setLikes(likesData)

        // Fetch photos
        const photosData = await api.photos.list(user.id);
        setPhotos(photosData);

        // Fetch profile answers
        const answersData = await api.users.getProfileAnswers()
        setProfileAnswers(answersData.answers || {})

      } catch (error) {
        console.error('Failed to fetch profile data:', error)
      }
    }
    fetchProfileData()
  }, [user?.id, setProfile])

  const handleSave = async () => {
    setSaving(true)
    try {
      const updatedProfile = {
        display_name: formData.display_name || null,
        bio: formData.bio || null,
        birth_year: formData.birth_year ? Number(formData.birth_year) : null,
        gender: (formData.gender || null) as 'male' | 'female' | 'other' | null,
        country: formData.country || null,
        city: formData.city || null,
        mbti: formData.mbti || null,
        interests: formData.interests.split(',').map((s) => s.trim()).filter(Boolean),
        languages: formData.languages.split(',').map((s) => s.trim()).filter(Boolean),
        preferred_gender: (formData.preferred_gender || 'any') as 'male' | 'female' | 'any',
        preferred_age_min: formData.preferred_age_min,
        preferred_age_max: formData.preferred_age_max,
      }
      const saved = await api.users.updateProfile(updatedProfile)
      setProfile(saved)

      // Save profile answers
      const answersToSave = ALL_QUESTIONS
        .filter(q => profileAnswers[q.id])
        .map(q => ({
          question_id: q.id,
          answer: profileAnswers[q.id],
          stage: q.stage
        }))

      if (answersToSave.length > 0) {
        await api.users.updateProfileAnswers(answersToSave)
      }

      setIsEditing(false)
      showAlert(t('profile.saveSuccess') || '프로필이 저장되었습니다.')
    } catch (error) {
      console.error('Failed to save profile:', error)
      showAlert(t('profile.saveFailed') || '프로필 저장에 실패했습니다.')
    } finally {
      setSaving(false)
    }
  }

  const handleAnswerChange = (questionId: string, value: string) => {
    setProfileAnswers(prev => ({
      ...prev,
      [questionId]: value
    }))
  }

  const getStageQuestions = (stage: number): ProfileQuestion[] => {
    return ALL_QUESTIONS.filter(q => q.stage === stage)
  }

  const getStageTheme = (stage: number) => {
    const themes = {
      1: { bg: 'from-imagine-50 to-imagine-100', border: 'border-imagine-300', icon: '🧠', color: 'text-imagine-600' },
      2: { bg: 'from-hear-50 to-hear-100', border: 'border-hear-300', icon: '🎤', color: 'text-hear-600' },
      3: { bg: 'from-see-50 to-see-100', border: 'border-see-300', icon: '👁️', color: 'text-see-600' }
    }
    return themes[stage as 1 | 2 | 3] || themes[1]
  }

  const getAnsweredCount = (stage: number): number => {
    const questions = getStageQuestions(stage)
    return questions.filter(q => profileAnswers[q.id]?.trim()).length
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await api.photos.upload(file);
      if (result.success) {
        setPhotos([...photos, result.photo]);
      }
    } catch (error) {
      console.error('Failed to upload photo:', error);
      showAlert(t('profile.uploadFailed'));
    }
  };

  const handlePhotoDelete = async (photoId: number) => {
    if (!await showConfirm(t('profile.deleteConfirm'))) return;

    try {
      await api.photos.delete(photoId);
      setPhotos(photos.filter(p => p.id !== photoId));
    } catch (error) {
      console.error('Failed to delete photo:', error);
      showAlert(t('profile.deleteFailed'));
    }
  };


  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="card-title text-2xl">{t('profile.title')}</h2>
                {likeCount > 0 && (
                  <div className="text-sm text-base-content/60 mt-1">
                    {t('profile.likesReceived', { count: likeCount })}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {likeCount > 0 && (
                  <button
                    onClick={() => setShowLikes(!showLikes)}
                    className="btn btn-outline btn-sm rounded-full"
                  >
                    💕 {likeCount}
                  </button>
                )}
                {isEditing ? (
                  <>
                    <button
                      className="btn btn-ghost"
                      onClick={() => {
                        setIsEditing(false)
                        // Reset form data to original profile
                        setFormData({
                          display_name: profile?.display_name || user?.username || '',
                          bio: profile?.bio || '',
                          birth_year: profile?.birth_year || '',
                          gender: profile?.gender || '',
                          country: profile?.country || 'KR',
                          city: profile?.city || '',
                          mbti: profile?.mbti || '',
                          interests: profile?.interests?.join(', ') || '',
                          languages: profile?.languages?.join(', ') || 'ko',
                          preferred_gender: profile?.preferred_gender || 'any',
                          preferred_age_min: profile?.preferred_age_min || 20,
                          preferred_age_max: profile?.preferred_age_max || 40,
                        })
                      }}
                      disabled={saving}
                    >
                      {t('profile.cancel') || '취소'}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? <span className="loading loading-spinner loading-sm" /> : t('profile.save')}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-ghost"
                    onClick={() => setIsEditing(true)}
                  >
                    {t('profile.edit')}
                  </button>
                )}
              </div>
            </div>

            {/* 받은 좋아요 목록 */}
            {showLikes && likes.length > 0 && (
              <div className="bg-base-200/50 rounded-lg p-4 mb-4 max-h-48 overflow-y-auto">
                <h3 className="font-semibold mb-3">{t('profile.likedByTitle')}</h3>
                <div className="space-y-2">
                  {likes.map((like) => (
                    <div key={like.id} className="bg-base-100 p-3 rounded-lg flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="avatar">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-200 to-mint-200">
                            {like.picture ? (
                              <img src={like.picture} alt={like.username} />
                            ) : (
                              <span className="text-lg font-bold">{like.username?.[0]?.toUpperCase()}</span>
                            )}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{like.display_name || like.username}</p>
                          <p className="text-xs text-base-content/60">{like.country}</p>
                        </div>
                      </div>
                      {like.mbti && <span className="badge badge-sm badge-ghost">{like.mbti}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="divider" />

            {/* Photos Section */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3">{t('profile.myPhotos')} <span className="text-sm text-base-content/50">({photos.length}/5)</span></h3>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {photos.map(photo => (
                  <div key={photo.id} className="relative group aspect-square">
                    <img
                      // TODO: Replace with a proper R2 public URL
                      src={`https://pub-39620b025a5a454c87b3ec827f19ae5d.r2.dev/${photo.photo_url}`}
                      alt="Profile photo"
                      className="w-full h-full object-cover rounded-lg bg-base-200"
                    />
                    {isEditing && (
                      <button
                        onClick={() => handlePhotoDelete(photo.id)}
                        className="btn btn-xs btn-circle btn-error absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      >
                        ✕
                      </button>
                    )}
                    {photo.is_primary ? (
                      <div className="badge badge-primary badge-sm absolute bottom-1 left-1">{t('profile.primary')}</div>
                    ) : isEditing && (
                      <button className="btn btn-xs absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">{t('profile.setPrimary')}</button>
                    )}
                  </div>
                ))}
                {isEditing && photos.length < 5 && (
                  <label className="aspect-square flex items-center justify-center border-2 border-dashed rounded-lg cursor-pointer hover:bg-base-200 transition-colors">
                    <div className="text-center text-base-content/50">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" /></svg>
                      <p className="text-xs mt-1">{t('profile.addPhoto')}</p>
                    </div>
                    <input type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                  </label>
                )}
              </div>
            </div>

            <div className="divider" />

            <div className="space-y-4">
              {/* Display Name */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.displayName')}</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  disabled={!isEditing}
                />
              </div>

              {/* Bio */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.bio')}</span>
                </label>
                <textarea
                  className="textarea textarea-bordered h-24"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  disabled={!isEditing}
                  placeholder={t('profile.bioPlaceholder')}
                />
              </div>

              {/* Gender & Birth Year */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">{t('profile.gender')}</span>
                  </label>
                  <select
                    className="select select-bordered"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    disabled={!isEditing}
                  >
                    <option value="">{t('profile.selectOption')}</option>
                    <option value="male">{t('profile.male')}</option>
                    <option value="female">{t('profile.female')}</option>
                    <option value="other">{t('profile.other')}</option>
                  </select>
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">{t('profile.birthYear')}</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered"
                    value={formData.birth_year}
                    onChange={(e) => setFormData({ ...formData, birth_year: e.target.value })}
                    disabled={!isEditing}
                    placeholder="1990"
                    min={1950}
                    max={2010}
                  />
                </div>
              </div>

              {/* Country & City */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">{t('profile.country')}</span>
                  </label>
                  <select
                    className="select select-bordered"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    disabled={!isEditing}
                  >
                    <option value="KR">{t('profile.korea')}</option>
                    <option value="JP">{t('profile.japan')}</option>
                    <option value="TW">{t('profile.taiwan')}</option>
                    <option value="CN">{t('profile.china')}</option>
                    <option value="US">{t('profile.usa')}</option>
                    <option value="GB">{t('profile.uk')}</option>
                  </select>
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">{t('profile.city')}</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    disabled={!isEditing}
                    placeholder={t('profile.cityPlaceholder')}
                  />
                </div>
              </div>

              {/* MBTI */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.mbti')}</span>
                </label>
                <select
                  className="select select-bordered"
                  value={formData.mbti}
                  onChange={(e) => setFormData({ ...formData, mbti: e.target.value })}
                  disabled={!isEditing}
                >
                  <option value="">{t('profile.selectOption')}</option>
                  {['INTJ', 'INTP', 'ENTJ', 'ENTP', 'INFJ', 'INFP', 'ENFJ', 'ENFP',
                    'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ', 'ISTP', 'ISFP', 'ESTP', 'ESFP'].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                </select>
              </div>

              {/* Display Language */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.displayLanguage')}</span>
                </label>
                <select
                  className="select select-bordered"
                  value={i18n.language}
                  onChange={(e) => i18n.changeLanguage(e.target.value)}
                >
                  <option value="ko">{t('profile.languageKorean')}</option>
                  <option value="en">{t('profile.languageEnglish')}</option>
                  <option value="ja">{t('profile.languageJapanese')}</option>
                  <option value="zh-TW">{t('profile.languageChinese')}</option>
                </select>
              </div>

              {/* Interests */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.interests')}</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered"
                  value={formData.interests}
                  onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                  disabled={!isEditing}
                  placeholder={t('profile.interestsPlaceholder')}
                />
              </div>


              {/* Languages - Checkbox based selection */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.languages')}</span>
                  <span className="label-text-alt text-base-content/50">{t('profile.selectMultiple')}</span>
                </label>
                <div className="flex flex-wrap gap-3 p-2">
                  {[
                    { code: 'ko', label: t('profile.languageKorean') || '한국어' },
                    { code: 'en', label: t('profile.languageEnglish') || 'English' },
                    { code: 'ja', label: t('profile.languageJapanese') || '日本語' },
                    { code: 'zh', label: t('profile.languageChinese') || '中文' },
                  ].map(lang => {
                    const languageArray = formData.languages.split(',').map(s => s.trim()).filter(Boolean)
                    const isChecked = languageArray.includes(lang.code)

                    return (
                      <label key={lang.code} className="label cursor-pointer gap-2 flex-nowrap">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={isChecked}
                          onChange={(e) => {
                            const langs = formData.languages.split(',').map(s => s.trim()).filter(Boolean)
                            const newLangs = e.target.checked
                              ? [...langs, lang.code]
                              : langs.filter(l => l !== lang.code)
                            setFormData({ ...formData, languages: newLangs.join(', ') })
                          }}
                          disabled={!isEditing}
                        />
                        <span className="label-text">{lang.label}</span>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="divider">{t('profile.matchingPreferences')}</div>

              {/* Preferred Gender */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">{t('profile.preferredGender')}</span>
                </label>
                <select
                  className="select select-bordered"
                  value={formData.preferred_gender}
                  onChange={(e) => setFormData({ ...formData, preferred_gender: e.target.value })}
                  disabled={!isEditing}
                >
                  <option value="any">{t('profile.any')}</option>
                  <option value="male">{t('profile.male')}</option>
                  <option value="female">{t('profile.female')}</option>
                </select>
              </div>

              {/* Preferred Age Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">{t('profile.preferredMinAge')}</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered"
                    value={formData.preferred_age_min}
                    onChange={(e) => setFormData({ ...formData, preferred_age_min: Number(e.target.value) })}
                    disabled={!isEditing}
                    min={18}
                    max={80}
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium">{t('profile.preferredMaxAge')}</span>
                  </label>
                  <input
                    type="number"
                    className="input input-bordered"
                    value={formData.preferred_age_max}
                    onChange={(e) => setFormData({ ...formData, preferred_age_max: Number(e.target.value) })}
                    disabled={!isEditing}
                    min={18}
                    max={80}
                  />
                </div>
              </div>

              {/* Profile Questions Section */}
              <div className="divider">{t('profile.profileQuestions')}</div>
              <p className="text-sm text-base-content/60 mb-4">{t('profile.questionsDesc')}</p>

              {[1, 2, 3].map(stage => {
                const theme = getStageTheme(stage)
                const questions = getStageQuestions(stage)
                const answeredCount = getAnsweredCount(stage)
                const isExpanded = expandedStage === stage
                const stageName = t(`stages.${stage}.name`)
                const stageDesc = t(`stages.${stage}.description`)

                return (
                  <div key={stage} className={`collapse collapse-arrow bg-gradient-to-br ${theme.bg} rounded-xl border ${theme.border}`}>
                    <input
                      type="checkbox"
                      checked={isExpanded}
                      onChange={() => setExpandedStage(isExpanded ? null : stage)}
                    />
                    <div className="collapse-title">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{theme.icon}</span>
                        <div className="flex-1">
                          <h4 className={`font-bold ${theme.color}`}>
                            Stage {stage}: {stageName}
                          </h4>
                          <p className="text-sm text-base-content/60">{stageDesc}</p>
                        </div>
                        <div className="badge badge-outline">
                          {answeredCount}/{questions.length}
                        </div>
                      </div>
                    </div>
                    <div className="collapse-content">
                      <div className="space-y-4 pt-2">
                        {questions.map((q) => {
                          const questionKey = `stages.${stage}.questions.${q.id}`
                          const questionText = t(`${questionKey}.text`, { defaultValue: q.text })
                          const placeholder = t(`${questionKey}.placeholder`, { defaultValue: q.placeholder || '' })

                          return (
                            <div key={q.id} className="form-control">
                              <label className="label">
                                <span className="label-text font-medium">{questionText}</span>
                              </label>
                              <input
                                type="text"
                                className="input input-bordered bg-white/80"
                                value={profileAnswers[q.id] || ''}
                                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                disabled={!isEditing}
                                placeholder={isEditing ? placeholder : t('profile.questionsNotEditing')}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
